// START IMPORTS
import { useReport } from '@ltvco/refresh-lib/hooks';
import {
  ReactivationDaysLeft,
  uncancelBillingVariation2Value,
  uncancelBillingPbrFeatureName,
  uncancelBillingControlValue,
} from '@ltvco/refresh-lib/payments';
import { Stack, Box, useTheme } from '@ltvco/refresh-lib/theme';
import {
  isZeroed,
  useScrollToSectionOnNavigate,
  DateUtil,
} from '@ltvco/refresh-lib/utils';
import {
  DebugMenu,
  ReportLoading,
  useCreateDarkWeb,
  useDarkWebReport,
} from '@ltvco/refresh-lib/v1';
import {
  ReportFactory,
  FraudEmailOverview,
  ReportGeneratingScreen,
  ReportNavigationMobileMenu,
  ReportNavigationMenu,
  ReportNavigationMenuProps,
  ReportActionsWithDateUpdated,
  ReportRating,
  AdditionalDetailsSection,
  PotentialOwnerCta as PotentialOwnerCtaV2,
  ReportNullState,
  DarkWebBreachTips,
  DarkWebCoverage,
  DarkWebResults as DarkWebResultsV2,
  DarkWebScanFAQ as DarkWebFAQV2,
  EmailDarkWebVerify,
} from '@ltvco/refresh-lib/v2';
import { useFeatureValue, useFeatureIsOn } from '@ltvco/refresh-lib/vendors';
import { useState } from 'react';
import { useLocation } from 'react-router-dom';

import { ReportRouteProps } from 'components/reports/types';
import { getFraudEmailNavLinkDataV2 } from 'navLinkData/fraudNavLinkData';
// END IMPORTS

export function EmailFraudReportPage({
  permalink,
  firstReportScreen,
}: ReportRouteProps) {
  // START HOOKS_AND_QUERIES
  const queryResult = useReport(permalink);
  const theme = useTheme();
  const { hash, search } = useLocation();
  useScrollToSectionOnNavigate(hash, queryResult);
  // END HOOKS_AND_QUERIES

  // START STATE_MANAGEMENT
  const [isDarkWebSectionLoading, setIsDarkWebSectionLoading] = useState(false);
  const [showDarkWebEmailSection, setShowDarkWebEmailSection] = useState(false);
  const [, triggerUpdate] = useState(0);
  const uncancelFeatureValue = useFeatureValue(
    uncancelBillingPbrFeatureName,
    uncancelBillingControlValue
  );
  const uncancelFeatureIsOn = useFeatureIsOn(uncancelBillingPbrFeatureName);
  const isEmailFraudRedesign = useFeatureIsOn('RFRSH-4333');
  // END STATE_MANAGEMENT

  // START DERIVED_DATA_AND_UTILS
  const delayDarkWebLoading = 1000;
  const fallbackEmail =
    new URLSearchParams(search).get('email')?.toLowerCase() ?? '';
  const reportEmail =
    queryResult.data?.meta?.search_data?.email?.toLowerCase() ?? '';
  const darkWebEmail = isEmailFraudRedesign ? reportEmail || fallbackEmail : '';
  const { data: createDarkWebData } = useCreateDarkWeb({ email: darkWebEmail });
  const { data: darkWebData, isLoading: isDarkWebReportLoading } =
    useDarkWebReport({ email: darkWebEmail });
  const breaches = isEmailFraudRedesign
    ? darkWebData?.data?.breaches ?? []
    : [];
  const isDarkWebLoading = isDarkWebReportLoading || isDarkWebSectionLoading;
  const darkWebPermalink = createDarkWebData?.report?.permalink ?? permalink;
  const reportType = 'email-fraud';
  const zeroed = isZeroed(queryResult);
  // END DERIVED_DATA_AND_UTILS

  // START EVENT_HANDLERS
  function handleDarkWebSectionLoadingScreen() {
    setIsDarkWebSectionLoading(true);
    setShowDarkWebEmailSection(true);
    setTimeout(() => {
      setIsDarkWebSectionLoading(false);
    }, delayDarkWebLoading * 10);
  }
  // END EVENT_HANDLERS

  // START EARLY_RETURNS
  if (firstReportScreen?.isActive) {
    return (
      <ReportGeneratingScreen
        reportType="email-fraud"
        ready={!queryResult.isLoading}
        onComplete={() => {
          firstReportScreen?.onComplete();
          triggerUpdate((n) => n + 1);
        }}
      />
    );
  }

  if (queryResult.isLoading || queryResult.isError) {
    return <ReportLoading menuItems={2} />;
  }
  if (zeroed) {
    return <ReportNullState />;
  }
  // END EARLY_RETURNS

  // START DATA_PREPARATION
  const report = ReportFactory.create(queryResult?.data, 'email-fraud');

  const email = report.data.address?.toLowerCase() ?? fallbackEmail;
  document.title = `${email} - BeenVerified`;

  const fraudEmailNavLinkDataV2 = getFraudEmailNavLinkDataV2();

  const date = new DateUtil();
  const reportUpdateDate = report.data.meta?.updated_at
    ? date.parseDateFromString(
        report.data.meta?.updated_at,
        'yyyy-MM-dd',
        'yyyy-MM-dd HH:mm:ss ZZZ'
      )
    : '';

  if (!report) {
    return <ReportLoading menuItems={2} />;
  }

  if (report?.data?.isEmpty) {
    return <ReportNullState />;
  }

  const {
    isCatchAll,
    isFrequentComplainer,
    activity,
    isLeaked,
    validityScore,
    isRecentAbuse,
    domain,
  } = report.data;

  const { isRiskyTld, isDmarcRecord, isDnsValid, mxRecords, suggestedDomain } =
    domain || {};

  const additionalDetails = (
    <AdditionalDetailsSection
      isRecentAbuse={isRecentAbuse}
      isDmarcRecord={isDmarcRecord}
      typo={suggestedDomain}
      isCatchAll={isCatchAll}
      isFrequentComplainer={isFrequentComplainer}
      isLeaked={isLeaked}
      isRiskyTld={isRiskyTld}
      isDnsValid={isDnsValid}
      validityScore={validityScore}
      userActivity={activity}
      mxRecords={mxRecords}
    />
  );
  // END DATA_PREPARATION

  // START RENDER
  return (
    <Box>
      <Stack direction="row">
        <Box
          sx={{
            display: { xs: 'none', lg: 'block' },
          }}
        >
          <ReportNavigationMenu
            reportType="Email Fraud"
            headerTitle={email}
            menuItems={
              fraudEmailNavLinkDataV2 as ReportNavigationMenuProps['menuItems']
            }
          />
        </Box>
        <Box marginTop={0} width="100%">
          <Box
            sx={{
              height: 30,
              backgroundColor: theme.palette.background.default,
              position: 'sticky',
              marginBottom: '-10px',
              marginX: -1,
              top: 52,
              zIndex: 10,
              display: {
                xs: 'none',
                md: 'block',
              },
            }}
          />
          {uncancelFeatureIsOn &&
            uncancelFeatureValue === uncancelBillingVariation2Value && (
              <ReactivationDaysLeft
                direction="row"
                isInReport
                customPadding={'16px 0 12px 0'}
              />
            )}

          <ReportActionsWithDateUpdated
            reportType={reportType}
            reportTitle="Fraud Scan"
            dateUpdated={reportUpdateDate}
            reportUpgraded={false}
          />

          <FraudEmailOverview
            reportData={report.data}
            breaches={breaches}
            loading={isDarkWebLoading}
          />

          <Stack spacing={2}>
            <PotentialOwnerCtaV2 email={email} reportType="email" />
            {isEmailFraudRedesign && (
              <>
                {additionalDetails}
                {showDarkWebEmailSection ? (
                  <DarkWebResultsV2
                    email={email}
                    breaches={breaches}
                    loading={isDarkWebLoading}
                    delayDuration={delayDarkWebLoading}
                    onFraudReport
                  />
                ) : (
                  <EmailDarkWebVerify
                    email={email}
                    sectionTitle="Data Breaches"
                    sectionId="dark-web-breaches-section"
                    handleDarkWebLoadingScreen={
                      handleDarkWebSectionLoadingScreen
                    }
                  />
                )}
                <DarkWebBreachTips permalink={darkWebPermalink} noMonitoring />
                <DarkWebCoverage />
                <DarkWebFAQV2 />
              </>
            )}
            {!isEmailFraudRedesign && additionalDetails}
            <ReportRating
              rating={report.data.meta?.rating || null}
              report_type={reportType}
            />
          </Stack>
        </Box>
      </Stack>
      <ReportNavigationMobileMenu
        reportType="Fraud Scan"
        headerTitle={email}
        menuItems={
          fraudEmailNavLinkDataV2 as ReportNavigationMenuProps['menuItems']
        }
      />
      <DebugMenu menuItems={report.data.rawData.debug_menu} />
    </Box>
  );
  // END RENDER
}
